import { useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import type { ServiceTermsBlock } from '@quality-vet/contracts/service-terms';
import { ModalCloseButton } from '@quality-vet/ui/modal-close-button';

import { ExpertGroundedAiPanel } from './expert-grounded-ai-panel';
import { useModalDialog } from './use-modal-dialog';

const linkPattern = /((?:https?:\/\/|www\.)[^\s]+|[\w.+-]+@[\w.-]+\.[A-Za-z]{2,})/g;

interface TermsSection {
  heading?: Extract<ServiceTermsBlock, { type: 'heading' }>;
  blocks: ServiceTermsBlock[];
}

function linkedText(value: string): ReactNode {
  return value.split(linkPattern).map((part, index) => {
    if (!part) return null;
    if (/^[\w.+-]+@[\w.-]+\.[A-Za-z]{2,}$/.test(part)) {
      return (
        <a className="text-link" href={`mailto:${part}`} key={`${part}-${index}`}>
          {part}
        </a>
      );
    }
    if (/^(?:https?:\/\/|www\.)/.test(part)) {
      const suffix = part.match(/[),.;:]+$/)?.[0] ?? '';
      const address = suffix ? part.slice(0, -suffix.length) : part;
      return (
        <span key={`${part}-${index}`}>
          <a
            aria-label={`${address} (opens in a new tab)`}
            className="text-link"
            href={address.startsWith('www.') ? `https://${address}` : address}
            rel="noreferrer noopener"
            target="_blank"
          >
            {address}
          </a>
          {suffix}
        </span>
      );
    }
    return part;
  });
}

function partitionSections(blocks: readonly ServiceTermsBlock[]): TermsSection[] {
  const sections: TermsSection[] = [];
  let current: TermsSection = { blocks: [] };

  for (const block of blocks) {
    if (block.type === 'heading' && block.level === 2) {
      if (current.heading || current.blocks.length > 0) sections.push(current);
      current = { heading: block, blocks: [] };
    } else {
      current.blocks.push(block);
    }
  }
  if (current.heading || current.blocks.length > 0) sections.push(current);
  return sections;
}

function TermsBlockView({ block }: { block: ServiceTermsBlock }) {
  switch (block.type) {
    case 'heading':
      return block.level === 3 ? (
        <h4 className="legal-subheading">{linkedText(block.text)}</h4>
      ) : (
        <h5 className="legal-minor-heading">{linkedText(block.text)}</h5>
      );
    case 'paragraph':
      return <p>{linkedText(block.text)}</p>;
    case 'list':
      return (
        <ul>
          {block.items.map((item, index) => (
            <li key={`${item}-${index}`}>{linkedText(item)}</li>
          ))}
        </ul>
      );
    case 'table':
      return (
        <div
          aria-label="Scrollable Service Terms table"
          className="legal-table-scroll"
          role="region"
          tabIndex={0}
        >
          <table>
            <caption className="visually-hidden">Service Terms details</caption>
            <thead>
              <tr>
                {block.headers.map((header, index) => (
                  <th key={`${header}-${index}`} scope="col">
                    {linkedText(header)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, rowIndex) => (
                <tr key={`row-${rowIndex}`}>
                  {row.map((cell, cellIndex) => (
                    <td key={`cell-${rowIndex}-${cellIndex}`}>{linkedText(cell)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
  }
}

interface ServiceTermsModule {
  readonly serviceTermsBlocks: readonly ServiceTermsBlock[];
  readonly serviceTermsEffectiveDate: string | null;
  readonly serviceTermsTitle: string;
}

interface TermsModalProps {
  open: boolean;
  onClose: () => void;
}

export function TermsModal({ open, onClose }: TermsModalProps) {
  const [terms, setTerms] = useState<ServiceTermsModule>();
  const [termsError, setTermsError] = useState('');
  const dialogRef = useModalDialog<HTMLElement>(open, onClose);

  useEffect(() => {
    if (!open || terms) return;
    let active = true;
    setTermsError('');
    void import('@quality-vet/contracts/service-terms')
      .then((module) => {
        if (active) setTerms(module);
      })
      .catch(() => {
        if (active) setTermsError('The Privacy & Service Terms could not be loaded.');
      });
    return () => {
      active = false;
    };
  }, [open, terms]);

  const serviceTermsTitle = terms?.serviceTermsTitle ?? 'Privacy & Service Terms';
  const serviceTermsEffectiveDate = terms?.serviceTermsEffectiveDate;
  const termsSections = terms ? partitionSections(terms.serviceTermsBlocks) : [];

  return createPortal(
    <div
      className="legal-backdrop"
      data-modal-layer
      hidden={!open}
      role="presentation"
      onClick={onClose}
    >
      <section
        id="service-terms-dialog"
        aria-labelledby="service-terms-title"
        aria-modal="true"
        className="legal-modal"
        ref={dialogRef}
        role="dialog"
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
      >
        <header className="legal-modal-header">
          <div>
            <p className="brand-name eyebrow">quality.vet</p>
            <h2 id="service-terms-title">{serviceTermsTitle}</h2>
            {serviceTermsEffectiveDate ? (
              <p className="legal-effective">Effective {serviceTermsEffectiveDate}</p>
            ) : null}
          </div>
          <ModalCloseButton className="legal-close" label="Close Service Terms" onClick={onClose} />
        </header>
        <div aria-busy={!terms && !termsError} className="legal-modal-content">
          {!terms && !termsError ? (
            <p aria-atomic="true" aria-live="polite" role="status">
              Loading Privacy &amp; Service Terms...
            </p>
          ) : null}
          {termsError ? (
            <p className="legal-error" role="alert">
              {termsError}
            </p>
          ) : null}
          {termsSections.map((section, index) => {
            const sectionKey = `${section.heading?.text ?? 'introduction'}-${index}`;
            const sectionContent = (
              <section className="legal-section" key={sectionKey}>
                {section.heading ? <h3>{linkedText(section.heading.text)}</h3> : null}
                {section.blocks.map((block, blockIndex) => (
                  <TermsBlockView block={block} key={`${sectionKey}-${blockIndex}`} />
                ))}
              </section>
            );
            return section.heading && /^general$/i.test(section.heading.text.trim()) ? (
              <div className="service-terms-general-layout" key={`general-${sectionKey}`}>
                {sectionContent}
                <ExpertGroundedAiPanel />
              </div>
            ) : (
              sectionContent
            );
          })}
        </div>
      </section>
    </div>,
    document.body,
  );
}
